import { ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import type {
  Confidence,
  DistressStage,
  EventKind,
  OwnershipType,
  SizeBand,
  SourceRef,
  SubSegment,
  WatchlistStage,
} from '../types';

// Shared M&A Radar atoms — the mna clone of wwd/tabs/_shared.tsx. ConfidenceValue is THE
// load-bearing component: every fact renders through it so the four confidence tiers
// (types.ts data-quality core, identical contract to WWD) are visually unmistakable.

/**
 * Renders a fact according to its confidence tier (WWD contract):
 *  - confirmed      → normal text
 *  - estimated      → orange "(est.) <value> — <basis>"
 *  - reported       → value with the inline linked source
 *  - not_disclosed  → grey italic "Not publicly disclosed" (also when value is null)
 */
export function ConfidenceValue({
  value,
  confidence,
  estBasis,
  source,
}: {
  value: string | null;
  confidence: Confidence;
  estBasis?: string | null;
  source?: SourceRef | null;
}) {
  if (confidence === 'not_disclosed' || value === null) {
    return <span className="text-muted italic opacity-70">Not publicly disclosed</span>;
  }
  if (confidence === 'estimated') {
    return (
      <span className="text-amber-500" title={estBasis ?? undefined}>
        <span className="text-xs">(est.) </span>
        {value}
        {estBasis && <span className="text-xs text-muted"> — {estBasis}</span>}
      </span>
    );
  }
  if (confidence === 'reported') {
    return (
      <span>
        {value}
        <span className="ml-1 text-[0.65rem] uppercase tracking-wider text-muted font-(family-name:--font-ui)">
          reported
        </span>
        {source && (
          <>
            {' '}
            <SourceLink source={source} />
          </>
        )}
      </span>
    );
  }
  return <span>{value}</span>;
}

/** Small external-link anchor pointing at the mandated deep link of a SourceRef. */
export function SourceLink({ source }: { source: SourceRef }) {
  const title = source.documentTitle + (source.pageRef ? ' — ' + source.pageRef : '');
  return (
    <a
      href={source.url}
      target="_blank"
      rel="noreferrer"
      title={title}
      aria-label={'Source: ' + title}
      className="inline-flex items-center align-middle text-accent hover:text-accent-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
    >
      <ExternalLink className="h-3.5 w-3.5" aria-hidden />
    </a>
  );
}

/** ISO-3166 alpha-2 → regional-indicator emoji. Pure function, no dependency. */
export function flagEmoji(countryCode: string): string {
  const cc = countryCode.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return '';
  return String.fromCodePoint(
    0x1f1e6 + (cc.charCodeAt(0) - 65),
    0x1f1e6 + (cc.charCodeAt(1) - 65)
  );
}

export function Flag({ countryCode }: { countryCode: string | null }) {
  if (!countryCode) return null;
  const emoji = flagEmoji(countryCode);
  if (!emoji) return null;
  return (
    <span role="img" aria-label={countryCode} title={countryCode}>
      {emoji}
    </span>
  );
}

/* ---------- enum label maps ---------- */

export const SUB_SEGMENT_LABELS: Record<SubSegment, string> = {
  drilling_equipment_oem: 'Drilling equipment OEM',
  components: 'Components',
  aftermarket_service: 'Aftermarket & service',
  automation_digital: 'Automation & digital',
  rig_electrification: 'Rig electrification',
  mining_equipment_supply: 'Mining equipment & supply',
};

export const SUB_SEGMENT_SHORT: Record<SubSegment, string> = {
  drilling_equipment_oem: 'OEM',
  components: 'Components',
  aftermarket_service: 'Aftermarket',
  automation_digital: 'Automation',
  rig_electrification: 'Electrification',
  mining_equipment_supply: 'Mining',
};

export const OWNERSHIP_LABELS: Record<OwnershipType, string> = {
  public: 'Public',
  pe_owned: 'PE-owned',
  family_private: 'Family / private',
  conglomerate_division: 'Conglomerate division',
  state_linked: 'State-linked',
  unknown: 'Unknown',
};

export const SIZE_BAND_LABELS: Record<SizeBand, string> = {
  micro: 'Micro (<$10M)',
  small: 'Small ($10–50M)',
  mid: 'Mid ($50–250M)',
  large: 'Large ($250M+)',
  unknown: 'Size unknown',
};

export const SIZE_BAND_SHORT: Record<SizeBand, string> = {
  micro: 'Micro',
  small: 'Small',
  mid: 'Mid',
  large: 'Large',
  unknown: '?',
};

export const DISTRESS_LABELS: Record<DistressStage, string> = {
  none: 'No distress',
  early_signal: 'Early signal',
  pre_distress: 'Pre-distress',
  in_process: 'In process',
  resolved: 'Resolved',
};

/** Semantic hex map (WWD WELL_STATUS_COLORS idiom) — used by the Radar scatter + chips. */
export const DISTRESS_COLORS: Record<DistressStage, string> = {
  none: '#64748b',
  early_signal: '#eab308',
  pre_distress: '#f97316',
  in_process: '#ef4444',
  resolved: '#14b8a6',
};

export const WATCHLIST_LABELS: Record<WatchlistStage, string> = {
  radar: 'Radar',
  watch: 'Watch',
  qualified: 'Qualified',
  engaged: 'Engaged',
  passed: 'Passed',
};

const WATCHLIST_STYLES: Record<WatchlistStage, string> = {
  radar: 'border-border text-muted',
  watch: 'border-accent-info text-accent-info',
  qualified: 'border-accent text-accent',
  engaged: 'border-score-high text-score-high',
  passed: 'border-border text-muted opacity-60',
};

/* ---------- event-kind grouping (badge label + color per category) ---------- */

type EventCategory = 'insolvency' | 'pre_distress' | 'ownership' | 'sector' | 'sale_process' | 'fit';

const EVENT_CATEGORY: Record<EventKind, EventCategory> = {
  bankruptcy_filing: 'insolvency',
  restructuring_plan: 'insolvency',
  receivership: 'insolvency',
  asset_auction_363: 'insolvency',
  emergence: 'insolvency',
  liquidation: 'insolvency',
  case_milestones: 'insolvency',
  going_concern_flag: 'pre_distress',
  covenant_breach: 'pre_distress',
  credit_downgrade: 'pre_distress',
  debt_distressed: 'pre_distress',
  refinancing_wall: 'pre_distress',
  auditor_resignation: 'pre_distress',
  mass_layoffs: 'pre_distress',
  delisting_notice: 'pre_distress',
  accounts_overdue: 'pre_distress',
  pe_hold_mature: 'ownership',
  succession_signal: 'ownership',
  activist_stake: 'ownership',
  strategic_review: 'ownership',
  ipo_withdrawn: 'ownership',
  non_core_designation: 'ownership',
  continuation_vehicle: 'ownership',
  acquisition_agreed: 'ownership',
  merger_remedy_divestiture: 'sector',
  competitor_exit: 'sector',
  consolidation_wave: 'sector',
  sale_process_reported: 'sale_process',
  banker_mandate_reported: 'sale_process',
  product_launch: 'fit',
  major_contract_win_loss: 'fit',
  key_customer_change: 'fit',
  leadership_change: 'fit',
  certification_gained: 'fit',
  facility_change: 'fit',
  ownership_change: 'fit',
};

const EVENT_CATEGORY_STYLES: Record<EventCategory, string> = {
  insolvency: 'border-accent-danger text-accent-danger',
  pre_distress: 'border-accent-warn text-accent-warn',
  ownership: 'border-accent-info text-accent-info',
  sector: 'border-border-strong text-muted',
  sale_process: 'border-accent text-accent',
  fit: 'border-border text-muted',
};

export function eventKindLabel(kind: EventKind): string {
  return kind.replace(/_/g, ' ');
}

/* ---------- badges ---------- */

const badgeBase =
  'inline-block whitespace-nowrap rounded-(--radius-sm) border bg-surface-elevated px-2 py-0.5 ' +
  'font-(family-name:--font-ui) text-[0.65rem] font-medium uppercase tracking-[0.12em]';

export function EventKindBadge({ kind }: { kind: EventKind }) {
  return <span className={cn(badgeBase, EVENT_CATEGORY_STYLES[EVENT_CATEGORY[kind]])}>{eventKindLabel(kind)}</span>;
}

export function SubSegmentBadge({ segment }: { segment: SubSegment }) {
  return (
    <span title={SUB_SEGMENT_LABELS[segment]} className={cn(badgeBase, 'border-accent text-accent')}>
      {SUB_SEGMENT_SHORT[segment]}
    </span>
  );
}

export function DistressChip({ stage }: { stage: DistressStage }) {
  if (stage === 'none') return null;
  return (
    <span
      className={cn(badgeBase, 'inline-flex items-center gap-1.5')}
      style={{ borderColor: DISTRESS_COLORS[stage], color: DISTRESS_COLORS[stage] }}
    >
      <span
        aria-hidden
        className="inline-block h-1.5 w-1.5 rounded-full"
        style={{ backgroundColor: DISTRESS_COLORS[stage] }}
      />
      {DISTRESS_LABELS[stage]}
    </span>
  );
}

export function WatchlistBadge({ stage }: { stage: WatchlistStage | null }) {
  if (!stage) return null;
  return <span className={cn(badgeBase, WATCHLIST_STYLES[stage])}>{WATCHLIST_LABELS[stage]}</span>;
}

export function OwnershipBadge({ ownershipType, peSponsor }: { ownershipType: OwnershipType; peSponsor?: string | null }) {
  return (
    <span
      title={peSponsor ? `Sponsor: ${peSponsor}` : undefined}
      className={cn(badgeBase, 'border-border-strong text-muted')}
    >
      {OWNERSHIP_LABELS[ownershipType]}
    </span>
  );
}

/* ---------- score atoms ---------- */

/**
 * Hover help for every rendered fit number. Describes the MECHANIC, not the current
 * weights — dimensions and weights live in the DB-versioned rubric (admin Rubric tab)
 * and would go stale here.
 */
export const FIT_HELP =
  'Fit score (0–100): how well this company matches the acquisition thesis. ' +
  'The AI scores each dimension of the active rubric 0–100, and every dimension must cite ' +
  'at least one recorded company fact or event — uncited scores are rejected. ' +
  'The weighted dimension sum is then multiplied by the company’s highest sub-segment weight. ' +
  'Scores are stamped with the rubric version and refresh when the rubric changes or a ' +
  'fit-affecting event (ownership, products, customers, leadership…) is recorded. ' +
  'See the Rubric tab for the current dimensions, weights and scoring guidance.';

export function scoreTone(score: number): string {
  if (score >= 70) return 'text-score-high';
  if (score >= 40) return 'text-score-mid';
  return 'text-score-low';
}

/** Compact "fit 82 / avail 41" pair used on cards and lists. */
export function ScorePair({ fit, availability }: { fit: number | null; availability: number | null }) {
  return (
    <span className="inline-flex items-baseline gap-3 font-(family-name:--font-ui) [font-variant-numeric:tabular-nums]">
      <span title={FIT_HELP} className="cursor-help">
        <span className="text-[0.65rem] uppercase tracking-[0.12em] text-muted">Fit </span>
        {fit === null ? (
          <span className="text-muted">—</span>
        ) : (
          <span className={cn('text-base font-semibold', scoreTone(fit))}>{fit}</span>
        )}
      </span>
      <span>
        <span className="text-[0.65rem] uppercase tracking-[0.12em] text-muted">Avail </span>
        {availability === null ? (
          <span className="text-muted">—</span>
        ) : (
          <span className={cn('text-base font-semibold', scoreTone(availability))}>{availability}</span>
        )}
      </span>
    </span>
  );
}

/** Signed 30-day delta, colored by direction; renders nothing when null/zero. */
export function DeltaBadge({ delta, label }: { delta: number | null; label: string }) {
  if (delta === null || delta === 0) return null;
  const up = delta > 0;
  return (
    <span
      title={`${label} 30-day change`}
      className={cn(
        'font-(family-name:--font-ui) text-xs font-semibold [font-variant-numeric:tabular-nums]',
        up ? 'text-score-high' : 'text-score-low'
      )}
    >
      {up ? '▲' : '▼'} {up ? '+' : ''}
      {delta}
    </span>
  );
}

/* ---------- formatting ---------- */

export function fmtDate(iso: string): string {
  return iso.length > 10 ? iso.slice(0, 10) : iso;
}

export function fmtRevenueUsdM(v: number): string {
  return v >= 1000 ? `$${(v / 1000).toFixed(1)}B` : `$${Math.round(v)}M`;
}
